import { useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { getEpisodesStartingOnDate } from "../../utils/scheduleUtils";
import ClockTimeModal from "./ClockTimeModal";
import "../../styles/ScheduleCalendar.scss";

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const getMondayOfDate = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
};

const getMondayOfCurrentWeek = () => getMondayOfDate(new Date());

const getInitialWeekOffset = (episodes) => {
    const dates = (episodes || [])
        .filter(e => e.start_date_time)
        .map(e => new Date(e.start_date_time).getTime());
    if (dates.length === 0) return 0;
    const weekBeforeFirst = getMondayOfDate(new Date(Math.min(...dates)));
    weekBeforeFirst.setDate(weekBeforeFirst.getDate() - 7);
    const diffMs = weekBeforeFirst - getMondayOfCurrentWeek();
    return Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));
};

const ScheduleCalendar = ({ episodes, onEpisodeUpdate }) => {
    const [draggedEpisode, setDraggedEpisode] = useState(null);
    const [timeEpisode, setTimeEpisode] = useState(null);
    const [weekOffset, setWeekOffset] = useState(() => getInitialWeekOffset(episodes));

    const base = getMondayOfCurrentWeek();
    const startDate = new Date(base);
    startDate.setDate(base.getDate() + weekOffset * 7);

    // 8 weeks = 56 days, all starting on Monday — no empty leading cells needed
    const gridDays = Array.from({ length: 56 }, (_, i) => {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        return d;
    });

    const endDate = gridDays[55];

    const formatRangeLabel = (start, end) => {
        if (start.getFullYear() === end.getFullYear()) {
            return `${start.getDate()} ${MONTH_NAMES[start.getMonth()]} – ${end.getDate()} ${MONTH_NAMES[end.getMonth()]} ${end.getFullYear()}`;
        }
        return `${start.getDate()} ${MONTH_NAMES[start.getMonth()]} ${start.getFullYear()} – ${end.getDate()} ${MONTH_NAMES[end.getMonth()]} ${end.getFullYear()}`;
    };

    const handleDragStart = (e, episode) => {
        setDraggedEpisode(episode);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e, date) => {
        e.preventDefault();
        if (!draggedEpisode || !onEpisodeUpdate) return;

        const existingStart = new Date(draggedEpisode.start_date_time);
        const newDate = new Date(date);
        newDate.setHours(existingStart.getHours());
        newDate.setMinutes(existingStart.getMinutes());

        onEpisodeUpdate(draggedEpisode.id, 'start_date_time', newDate.toISOString().slice(0, 16));
        setDraggedEpisode(null);
    };

    const handleDragEnd = () => setDraggedEpisode(null);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Chip numbers reflect release order (earliest first), so they update as episodes are dragged around.
    const orderNumbers = {};
    [...(episodes || [])]
        .filter(e => e.start_date_time && !isNaN(new Date(e.start_date_time).getTime()))
        .sort((a, b) => new Date(a.start_date_time) - new Date(b.start_date_time))
        .forEach((e, i) => { orderNumbers[e.id] = i + 1; });

    const formatTime = (value) =>
        new Date(value).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

    const renderDay = (date, index) => {
        const episodesOnDate = getEpisodesStartingOnDate(date, episodes);
        const isToday = date.toDateString() === today.toDateString();
        const dayContent = (
            <>
                <div className="calendar-month-label">
                    {MONTH_NAMES[date.getMonth()]}
                </div>
                <div className="day-number">{date.getDate()}</div>
                {episodesOnDate.length > 0 && (
                    <div>
                        <span className="episode-order">{orderNumbers[episodesOnDate[0].id]}</span>
                        <span className="episode-order-time">{formatTime(episodesOnDate[0].start_date_time)}</span>
                    </div>
                )}
            </>
        );

        if (episodesOnDate.length > 0) {
            return (
                <OverlayTrigger
                    key={index}
                    placement="top"
                    delay={{ show: 250, hide: 400 }}
                    overlay={
                        <Tooltip>{episodesOnDate.map(e => e.title).join(', ')}</Tooltip>
                    }
                >
                    <div
                        style={{
                            borderLeft: '5px solid ' + episodesOnDate[0].pivot.color,
                            cursor: onEpisodeUpdate ? 'grab' : 'default',
                        }}
                        className={`calendar-day has-episodes ${isToday ? 'is-today' : ''}`}
                        draggable={!!onEpisodeUpdate}
                        onDragStart={(e) => handleDragStart(e, episodesOnDate[0])}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, date)}
                        onClick={() => onEpisodeUpdate && setTimeEpisode(episodesOnDate[0])}
                        role={onEpisodeUpdate ? 'button' : undefined}
                        tabIndex={onEpisodeUpdate ? 0 : undefined}
                        onKeyDown={(e) => { if (onEpisodeUpdate && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setTimeEpisode(episodesOnDate[0]); } }}
                    >
                        {dayContent}
                        {episodesOnDate.length > 1 && (
                            <Badge bg="danger" className="episode-badge">
                                {episodesOnDate.length}
                            </Badge>
                        )}
                    </div>
                </OverlayTrigger>
            );
        }

        return (
            <div
                key={index}
                className={`calendar-day ${isToday ? 'is-today' : ''}`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, date)}
            >
                {dayContent}
            </div>
        );
    };

    return (
        <Card>
            <Card.Body>
                <div className="calendar-controls mb-3 d-flex justify-content-between align-items-center">
                    <Button variant="outline-primary" size="sm" onClick={() => setWeekOffset(w => w - 1)}>
                        <FaChevronLeft />
                    </Button>
                    <h5 className="mb-0">
                        {formatRangeLabel(startDate, endDate)}
                    </h5>
                    <Button variant="outline-primary" size="sm" onClick={() => setWeekOffset(w => w + 1)}>
                        <FaChevronRight />
                    </Button>
                </div>

                <div className="calendar-grid">
                    <div className="calendar-header-row">
                        {DAY_NAMES.map(d => (
                            <div key={d} className="calendar-day-header">{d}</div>
                        ))}
                    </div>
                    <div className="calendar-days-grid">
                        {gridDays.map((date, i) => renderDay(date, i))}
                    </div>
                </div>

                <div className="calendar-legend mt-3">
                    <div className="d-flex gap-3 justify-content-center flex-wrap">
                        <div className="legend-item">
                            <span className="legend-box bg-success"></span>
                            <span className="legend-label">Episode Release Date</span>
                        </div>
                        <div className="legend-item">
                            <span className="legend-box border-primary"></span>
                            <span className="legend-label">Today</span>
                        </div>
                    </div>
                </div>
            </Card.Body>
            <ClockTimeModal
                show={!!timeEpisode}
                episode={timeEpisode && (episodes || []).find(e => e.id === timeEpisode.id)}
                number={timeEpisode ? orderNumbers[timeEpisode.id] : null}
                onClose={() => setTimeEpisode(null)}
                onSave={(id, value) => onEpisodeUpdate(id, 'start_date_time', value)}
            />
        </Card>
    );
};

export default ScheduleCalendar;
